import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import type { StudyBlockSnapshot } from "@/components/studyos/engine-v2/store/engine-store";
import { preparePrintBlocks } from "@/components/studyos/print/prepare-print-blocks";
import { PrintSessionView } from "@/components/studyos/print/print-session-view";
import type { PrintContentMode } from "@/components/studyos/print/types";
import { specToStudySession } from "@/components/studyos/spec-to-session";
import { Button } from "@/components/ui/button";
import { versionAttemptsListQueryOptions, versionQueryOptions } from "@/hooks/query/thread";
import { formatDate } from "@/lib/format";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

import type { AttemptRecord } from "../-components/attempt/attempt-record-types";
import { deriveAttemptInsights } from "../-components/attempt/attempt-utils";

export const Route = createFileRoute("/learn/$versionId/print")({
	component: PrintPage,
	validateSearch: z.object({
		mode: z.enum(["blank", "answer", "attempt"]).default("blank"),
		attemptId: z.string().optional(),
		blockId: z.string().optional(),
	}),
	head: () =>
		noIndexSeo({
			title: m.print_title(),
			description: "",
			pathname: "",
		}),
});

function PrintPage() {
	const { versionId } = Route.useParams();
	const { mode, attemptId, blockId } = Route.useSearch();
	const readyRef = useRef<HTMLDivElement>(null);
	const [ready, setReady] = useState(false);

	// Fetch version data
	const access = useSuspenseQuery(versionQueryOptions({ versionId })).data;
	const title = access.share?.title ?? m.learn_untitledSession();

	// Fetch attempts for attempt mode
	const { data: attempts } = useQuery({
		...versionAttemptsListQueryOptions({ versionId, enabled: mode === "attempt" }),
		enabled: mode === "attempt",
	});

	// Convert version output to session blocks
	const sessionBlocks = useMemo(() => {
		const output = access.status === "ok" ? access.version?.output : null;
		if (output?.type !== "ui" || !output.ui) return undefined;
		return specToStudySession(output.ui).blocks;
	}, [access]);

	// Find the specific attempt if in attempt mode
	const attempt = useMemo(() => {
		if (mode !== "attempt" || !attemptId || !attempts) return null;
		return (attempts as AttemptRecord[]).find((a) => a.id === attemptId) ?? null;
	}, [mode, attemptId, attempts]);

	// Prepare blocks for the selected mode
	const blocks = useMemo(() => {
		if (!sessionBlocks) return [];

		const snapshots =
			mode === "attempt" && attempt?.engineState?.blocks
				? (attempt.engineState.blocks as StudyBlockSnapshot[])
				: null;

		let prepared = preparePrintBlocks(sessionBlocks, snapshots, mode as PrintContentMode);

		// Filter to single block if blockId specified
		if (blockId) {
			prepared = prepared.filter((b) => b.id === blockId);
		}

		return prepared;
	}, [sessionBlocks, mode, attempt, blockId]);

	// Attempt stats for summary
	const attemptStats = useMemo(() => {
		if (mode !== "attempt" || !attempt?.engineState) return null;
		return deriveAttemptInsights(attempt.engineState);
	}, [mode, attempt]);

	// Mark as ready for Playwright
	useEffect(() => {
		if (blocks.length > 0) {
			setReady(true);
		}
	}, [blocks]);

	const handlePrint = useCallback(() => {
		window.print();
	}, []);

	if (!sessionBlocks || blocks.length === 0) {
		return (
			<div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
				Loading...
			</div>
		);
	}

	return (
		<div ref={readyRef} data-print-ready={ready ? "true" : undefined}>
			<PrintSessionView
				title={title}
				blocks={blocks}
				mode={mode as PrintContentMode}
				attemptStats={attemptStats}
				attemptDate={attempt?.createdAt ? formatDate(attempt.createdAt) : undefined}
			/>

			{/* Floating print button — hidden in print */}
			<div className="fixed right-6 bottom-6 z-50" data-no-print>
				<Button onClick={handlePrint} size="lg" className="shadow-lg">
					<PrinterIcon className="size-4" data-icon="inline-start" />
					{m.print_printButton()}
				</Button>
			</div>
		</div>
	);
}
